import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";
import { useCountDown, getMyanmarHour } from "@/helper/helper";
import { CONFIG_SHWE_HOURS } from "@/helper/const";
import PATH from "@config/PATH";
import iconShweHours from "@assets/web-v3-images/icons/shwe-hours.png";

/**
 * Icon Shwe Hours nổi trong trang Home: hiện đếm ngược tới/đến hết giờ vàng
 * (19:00–21:00 MMT). Bấm vào thì sang tab đấu giá đang chạy, riêng khi đã đủ 10
 * lượt và nhận thưởng thì gọi `onOpenReward(reward)` để Home mở lại popup thưởng.
 *
 * Quy tắc hiển thị:
 *   - Trong khung 10:00–22:00 MMT: luôn hiện, trượt vào sau 1s cho đỡ giật lúc
 *     vào trang.
 *   - Ngoài khung đó: KHÔNG render (không còn nhịp 30 phút hiện 30 giây, cũng
 *     không có animation trượt ra).
 *   - Người dùng bấm X thì trượt ra và ẩn tới khi tải lại trang.
 *
 * Dữ liệu `shweHourBidCount` được LayoutApp fetch một lần khi có userInfo (còn
 * dùng cho popup bên trang đấu giá) nên ở đây chỉ đọc từ store.
 */
/* ⚠️ DEBUG — XOÁ TRƯỚC KHI COMMIT.
   Giả lập "đã đoán đủ 10/10 và đã nhận thưởng" để bấm icon là mở được popup
   thưởng, khỏi phải chờ tới giờ vàng 19:00–21:00 MMT. Phải ép luôn `isInPeriod`
   vì điều kiện `receivedReward` yêu cầu đang trong giờ vàng.
   Đổi `reward_type` để xem đủ 4 loại ảnh thưởng:
   NORMAL_COIN | SUPER_COIN | FREE_TURN | PUZZLE_PIECE */
const DEBUG_FAKE_REWARD = false;
const DEBUG_BID_COUNT = null;

const ShweHoursIcon = ({ onOpenReward }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { countdown: countDown, isInPeriod: isInPeriodReal } = useCountDown();
  const { shweHourBidCount: shweHourBidCountReal } = useSelector(
    (state) => state.bid,
  );

  const isInPeriod = DEBUG_FAKE_REWARD || isInPeriodReal;
  const shweHourBidCount = DEBUG_FAKE_REWARD
    ? DEBUG_BID_COUNT
    : shweHourBidCountReal;

  // Đã đủ 10 lượt và đã nhận thưởng -> icon đang hiện "Congrats! +1 ..." (xem
  // renderTitle/renderCountDown), bấm vào thì xem lại popup thưởng.
  const receivedReward =
    isInPeriod &&
    shweHourBidCount?.totalBids >= 10 &&
    shweHourBidCount?.isReceived
      ? shweHourBidCount?.reward
      : null;

  /* Hai hiệu ứng gắn với TRẠNG THÁI, không chạy vô điều kiện — trang Home đã có
     sẵn vài animation vô hạn, thêm loop thường trực nữa là mất tiêu điểm.
       - Đang giờ vàng mà chưa đoán đủ 10 lượt -> nhún mời gọi vào đoán tiếp.
       - Hết giờ vàng và đã nhận thưởng -> quét sáng khoe phần thưởng. */
  const shouldNudge = isInPeriod && (shweHourBidCount?.totalBids ?? 0) < 10;
  const shouldShine = true;

  const [isVisible, setIsVisible] = useState(false);
  const [isUserClosed, setIsUserClosed] = useState(false);
  // Giờ hiện tại theo MMT, cập nhật mỗi phút để mốc 10h/22h tự đổi trạng thái.
  const [currentHour, setCurrentHour] = useState(() => getMyanmarHour());

  const isWithinShweHoursTime =
    currentHour >= CONFIG_SHWE_HOURS.startShowIcon &&
    currentHour < CONFIG_SHWE_HOURS.endShowIcon;

  const renderTitle = () => {
    if (isInPeriod) {
      if (
        shweHourBidCount?.totalBids < 10 &&
        shweHourBidCount?.totalBids > 0 &&
        shweHourBidCount?.isReceived === false
      ) {
        return t("shwe_hour.guess_more");
      }

      if (
        shweHourBidCount?.totalBids >= 10 &&
        (shweHourBidCount?.reward !== null ||
          shweHourBidCount?.reward !== undefined) &&
        shweHourBidCount?.isReceived === true
      ) {
        return t("shwe_hour.congrats");
      }

      return t("shwe_hour.ends_in");
    }

    return t("shwe_hour.starts_in");
  };

  const renderCountDown = () => {
    if (isInPeriod) {
      if (
        shweHourBidCount?.totalBids < 10 &&
        shweHourBidCount?.totalBids > 0 &&
        !shweHourBidCount?.isReceived
      ) {
        return `0${shweHourBidCount?.totalBids}/10`;
      }

      if (
        shweHourBidCount?.totalBids >= 10 &&
        (shweHourBidCount?.reward !== null ||
          shweHourBidCount?.reward !== undefined) &&
        shweHourBidCount?.isReceived
      ) {
        switch (shweHourBidCount?.reward.reward_type) {
          case "NORMAL_COIN":
            return `+1 Coin`;
          case "SUPER_COIN":
            return `+1 Super`;
          case "FREE_TURN":
            return `+1 Turn`;
          case "PUZZLE_PIECE":
            return `+1 Puzzle`;
          default:
            return "";
        }
      }
    }

    return countDown;
  };

  const handleClickIcon = (e) => {
    e?.stopPropagation();
    e?.preventDefault();
    // Đã có thưởng thì mở lại popup thưởng, chưa thì sang trang đấu giá để đoán.
    if (receivedReward) {
      onOpenReward?.(receivedReward);
      return;
    }
  };

  const handleCloseIcon = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsUserClosed(true); // ẩn tới khi tải lại trang
  };

  // Theo dõi giờ MMT theo thời gian thực — kiểm mỗi phút
  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentHour(getMyanmarHour());
    }, 60000);

    return () => clearInterval(intervalId);
  }, []);

  /* Trong khung giờ: trượt vào sau 1s (đỡ giật lúc vào trang). Người dùng bấm X
     thì trượt ra rồi nằm im tới khi reload. Animation CHỈ dùng cho hai trường
     hợp này — ngoài khung giờ icon bị bỏ khỏi DOM (xem early return dưới) nên
     không có slide in/out nào chạy. */
  useEffect(() => {
    if (!isWithinShweHoursTime || isUserClosed) {
      setIsVisible(false);
      return undefined;
    }

    const timeout = setTimeout(() => setIsVisible(true), 1000);
    return () => clearTimeout(timeout);
  }, [isWithinShweHoursTime, isUserClosed]);

  // Ngoài 10:00–22:00 MMT: ẩn hẳn (unmount), không animation, không nhịp hiện
  // 30 phút/lần như trước. `currentHour` tick mỗi phút nên qua mốc là tự đổi.
  if (!isWithinShweHoursTime) return null;

  /* Nằm TRONG cây DOM của trang Home (không portal ra body, không `fixed`): neo
     `absolute` theo khung <div relative> gốc của Home nên icon chỉ tồn tại trong
     vùng trang này và cuộn cùng nội dung. Vì ở trong lớp transform:scale của
     LayoutApp nên tự động thu nhỏ theo cả trang — khỏi cần useAppScale.
     Icon neo mép trái nên trạng thái ẩn đẩy sang trái (-150%) -> trượt VÀO TỪ
     BÊN TRÁI; ancestor `overflow-x-clip` của app-layout cắt phần tràn nên không
     sinh thanh cuộn ngang. */
  return (
    <div
      className={`absolute top-[35%] left-[8px] size-20 md:size-32 z-100 transition-[translate,opacity] duration-1000 ease-in-out min-[768px]:top-[40%] min-[768px]:left-[24px] ${
        isVisible
          ? "translate-x-0 opacity-100"
          : "pointer-events-none -translate-x-[150%] opacity-0"
      }`}
    >
      <div className="relative" onClick={handleClickIcon}>
        {/* <i
          className="fa fa-times absolute -right-[3px] -top-[3px] z-10 flex size-5 cursor-pointer items-center justify-center rounded-full bg-white/85 text-[16px] text-gray-500 transition-all duration-200 min-[576px]:size-[22px] min-[576px]:text-[18px] min-[992px]:hover:opacity-80"
          aria-hidden="true"
          onClick={handleCloseIcon}
        /> */}
        {/* Icon là PNG nền trong suốt (không có đế) nên phải dùng `drop-shadow`
            — bóng bám theo đúng silhouette; `box-shadow` sẽ vẽ bóng của khung
            chữ nhật rỗng. Hai lớp chồng nhau cho nổi trên nền cam: lớp sát chân
            đậm và gọn để tách khỏi nền, lớp dưới toả rộng tạo độ sâu. Phải viết
            bằng arbitrary property `[filter:...]` vì hai class `drop-shadow-*`
            chỉ đè lên nhau (cùng ghi vào --tw-drop-shadow) chứ không cộng dồn. */}
        <div
          className={`relative filter-[drop-shadow(0_1px_1.5px_rgba(90,17,6,0.55))_drop-shadow(0_5px_9px_rgba(90,17,6,0.45))] transition-transform duration-300 ease-in-out hover:scale-105 hover:cursor-pointer ${
            shouldNudge ? "animate-nudge motion-reduce:animate-none" : ""
          }`}
        >
          <div className="grenze absolute left-1/2 top-1/2 -translate-x-[53%] -translate-y-[170%] whitespace-nowrap text-center text-[8px] md:text-[12px] font-extrabold text-transparent bg-linear-to-b from-[#BC1B06] from-58% to-[#771A0D] bg-clip-text">
            {renderTitle()}
          </div>
          <div className="grenze absolute left-1/2 top-1/2 -translate-x-[45%] -translate-y-[29%] md:-translate-y-[25%] whitespace-nowrap text-center text-[8px] md:text-[12px] font-extrabold text-[#FDEFB0] text-shadow-[0_1.01px_1.71px_#B7220D]">
            {renderCountDown()}
          </div>
          <img src={iconShweHours} alt="" className="cursor-pointer" width={200} height={200} />
        </div>

        {/* Vệt sáng quét chéo, cắt theo ALPHA của chính icon (mask) nên chỉ sáng
            trên phần có hình, không lộ thành vệt chữ nhật ở khoảng trong suốt.
            URL ảnh do webpack băm tên nên phải truyền qua style, không viết
            được thành class Tailwind. Đứng SAU khối icon trong DOM để nằm trên
            nó, và ra NGOÀI div có `filter` để vệt sáng không bị đổ bóng theo. */}
        {shouldShine ? (
          <span
            aria-hidden="true"
            style={{
              mask: `url(${iconShweHours}) center/contain no-repeat`,
              WebkitMask: `url(${iconShweHours}) center/contain no-repeat`,
            }}
            className="pointer-events-none absolute inset-0 animate-shine bg-linear-[110deg,transparent_46%,rgba(255,255,255,0.95)_50%,transparent_54%] bg-size-[250%_100%] mix-blend-screen motion-reduce:animate-none"
          />
        ) : null}
      </div>
    </div>
  );
};

export default ShweHoursIcon;
